import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Copy,
  Check,
  RefreshCw,
  Plus,
  X,
  Bell,
  BellOff,
  AlertCircle,
  Send,
  Mail,
} from 'lucide-react';
import { LIMITS } from '@superchat/shared/constants';
import { useWorkspace } from '../lib/workspace.jsx';
import { useAuth } from '../lib/auth.jsx';
import { api } from '../lib/api.js';
import AppTypeCard from '../components/AppTypeCard.jsx';
import SlackCard from '../components/SlackCard.jsx';
import ResendCard from '../components/ResendCard.jsx';
import DeliveryStatusCard from '../components/DeliveryStatusCard.jsx';
import { enablePush, disablePush, currentSubscription, pushSupported } from '../lib/push.js';
import { desktopEnabled, setDesktopEnabled, requestPermission } from '../lib/notify.js';

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="link-button"
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/**
 * The identity block, written against the app it will actually run in.
 *
 * A generic example is the one nobody adapts. The keys shown differ by
 * platform because what is worth knowing about a Shopify store — which shop,
 * which plan, which app version — is not what is worth knowing about a web
 * app, and an agent reading the context rail can only see what the snippet
 * chose to send.
 */
function identitySnippet({ platform }) {
  const shopify = platform === 'shopify';

  const metadata = shopify
    ? [
        '      // Which store this is. The most useful field you can send:',
        '      // without it every merchant looks the same to an agent.',
        '      shop: session.shopDomain,        // "acme-supply.myshopify.com"',
        '      plan: session.shopPlan,          // "advanced"',
        '      appVersion: "3.4.1",',
        '      installedAt: session.installedAt,',
      ]
    : [
        '      plan: account.plan,              // "growth"',
        '      accountId: account.id,',
        '      appVersion: "3.4.1",',
      ];

  return [
    '<script>',
    '  window.SuperChat = window.SuperChat || {};',
    '  // …publicKey and apiBase from the snippet above…',
    '',
    shopify
      ? '  // Who is using your app right now. Rendered per merchant, per session.'
      : '  // Who this customer is. Every field is optional.',
    '  window.SuperChat.visitor = {',
    `    name: ${shopify ? 'session.contactName' : '"Ada Merchant"'},`,
    `    email: ${shopify ? 'session.contactEmail' : '"ada@example.com"'},`,
    '    metadata: {',
    ...metadata,
    '    },',
    '  };',
    '</script>',
  ].join('\n');
}

/**
 * Everything a merchant install needs: the loader snippet, the app's public
 * key, the origin allowlist that key is only honoured from, widget appearance,
 * and the agent's own push registration.
 */
export function IntegrationView() {
  const { apps, appIndex, activeApp, activeAppId, allApps, refreshApps, can, isAppOwner } =
    useWorkspace();

  // Read access shows the snippet and the keys; editing the allowlist, the
  // appearance or the broadcast test all need write.
  const canEdit = can('integration', 'write');
  const { user, updateUser } = useAuth();
  const [install, setInstall] = useState(null);
  const [origins, setOrigins] = useState([]);
  const [originDraft, setOriginDraft] = useState('');
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushError, setPushError] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [desktopOn, setDesktopOn] = useState(() => desktopEnabled());
  const [serverConfig, setServerConfig] = useState(null);
  const [emailSaving, setEmailSaving] = useState(null);
  const [emailResult, setEmailResult] = useState(null);
  const [emailTesting, setEmailTesting] = useState(false);

  // Tells us whether RESEND_API_KEY and EMAIL_FROM are actually set, so the
  // toggles can say why they will do nothing instead of silently not working.
  useEffect(() => {
    api.config().then(setServerConfig).catch(() => setServerConfig(null));
  }, []);

  const emailPrefs = user?.notifications?.email ?? {};

  const visitorSnippet = useMemo(
    () =>
      identitySnippet({ platform: activeApp?.platform }),
    [activeApp?.platform]
  );

  /**
   * Per-agent, not per-app: an agent covering four merchants wants one answer
   * to "email me when I'm away". Only the changed flag is sent, so the two
   * toggles never overwrite each other.
   */
  const setEmailPref = async (key, value) => {
    setEmailSaving(key);
    setEmailResult(null);
    try {
      const data = await api.auth.updateProfile({ notifications: { email: { [key]: value } } });
      updateUser(data.user);
    } catch (err) {
      setEmailResult({ ok: false, message: err.message });
    } finally {
      setEmailSaving(null);
    }
  };

  const sendTestEmail = async () => {
    setEmailTesting(true);
    setEmailResult(null);
    try {
      const result = await api.notify.emailTest(activeAppId);
      setEmailResult(
        result.sent > 0
          ? { ok: true, message: `Sent to ${result.to} from ${result.from}. Check your inbox.` }
          : {
              ok: false,
              message:
                result.reason ??
                'Resend accepted nothing. Check the API key and that the from-domain is verified.',
            }
      );
    } catch (err) {
      setEmailResult({ ok: false, message: err.message });
    } finally {
      setEmailTesting(false);
    }
  };

  const load = useCallback(async () => {
    if (!activeAppId) return;
    setError(null);
    try {
      setInstall(await api.apps.installSnippet(activeAppId));
    } catch (err) {
      setError(err.message);
    }
  }, [activeAppId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setOrigins(activeApp?.allowedOrigins ?? []);
    setSettings(activeApp?.settings ? { ...activeApp.settings } : null);
  }, [activeApp]);

  useEffect(() => {
    currentSubscription().then((sub) => setPushOn(Boolean(sub)));
  }, [activeAppId]);

  const saveApp = async (patch) => {
    setSaving(true);
    setError(null);
    try {
      await api.apps.update(activeAppId, patch);
      await refreshApps();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const addOrigin = (event) => {
    event.preventDefault();
    const value = originDraft.trim();
    if (!value) return;
    let normalized;
    try {
      normalized = new URL(value).origin;
    } catch {
      setError('An origin must be an absolute URL, e.g. https://app.your-domain.com');
      return;
    }
    if (origins.includes(normalized)) return;
    const next = [...origins, normalized];
    setOrigins(next);
    setOriginDraft('');
    saveApp({ allowedOrigins: next });
  };

  const removeOrigin = (origin) => {
    const next = origins.filter((o) => o !== origin);
    setOrigins(next);
    saveApp({ allowedOrigins: next });
  };

  const rotate = async () => {
    // Every deployed snippet using the old key stops working immediately.
    if (!window.confirm('Rotate the public key? Every deployed snippet must be updated.')) return;
    await api.apps.rotateKey(activeAppId);
    await refreshApps();
    await load();
  };

  const togglePush = async () => {
    setPushError(null);
    try {
      if (pushOn) {
        await disablePush(activeAppId);
        setPushOn(false);
      } else {
        await enablePush(activeAppId);
        setPushOn(true);
      }
    } catch (err) {
      setPushError(err.message);
    }
  };

  /**
   * Broadcasts to every visitor on this app — socket for whoever has the
   * widget open, Web Push for every registered visitor device. The user-alert
   * variant additionally force-opens their chat over any mute, so it is exactly
   * what a real one does.
   */
  const notifyVisitors = async (isEmergency) => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await api.notify.visitors(activeAppId, isEmergency);
      const live = result.socketTargets;
      const pushed = result.push?.sent ?? 0;

      const tabs = result.socketConnections ?? live;
      const parts = [];
      if (live > 0) {
        parts.push(
          `${live} visitor${live === 1 ? '' : 's'} live${tabs > live ? ` (${tabs} tabs)` : ''}`
        );
      }
      if (pushed > 0) parts.push(`${pushed} push device${pushed === 1 ? '' : 's'}`);

      setTestResult({
        ok: parts.length > 0,
        message:
          parts.length > 0
            ? `Delivered to ${parts.join(' + ')}${
                result.push?.pruned ? ` · pruned ${result.push.pruned} dead endpoint(s)` : ''
              }.`
            : result.pushEnabled
              ? 'No visitor online and no visitor device registered for push. Nothing delivered.'
              : 'No visitor online, and web push is not configured on this server.',
      });
    } catch (err) {
      setTestResult({ ok: false, message: err.message });
    } finally {
      setTesting(false);
    }
  };

  if (!activeApp) {
    return (
      <div className="empty">
        {allApps
          ? 'Integration settings belong to one app. Pick a single app in the switcher.'
          : 'Create an app to get an install snippet.'}
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">Integration</h1>
        <span className="page__sub">Embed the widget in each app you support</span>
      </header>

      <div className="page__body integration">
        {error && <div className="login__error">{error}</div>}
        {!canEdit && (
          <div className="result result--warn">
            Your access to this app is read-only for integration. You can copy the snippet and
            keys; changing origins, appearance or the key needs edit access.
          </div>
        )}

        <div className="integration__col">
          <div className="card">
            <div className="card__head">
              <span className="card__title">1 · Drop the loader into your app's admin</span>
              {install && <CopyButton value={install.snippet} />}
            </div>
            <pre className="code">{install?.snippet ?? 'Loading…'}</pre>
            <div className="card__foot">
              The bundle is served from a cacheable, CORS-open path. Add
              <code> data-service-worker="/superchat-sw.js"</code> if the merchant hosts the
              reference worker — that is what enables Web Push on their side.
            </div>
          </div>

          {/* Directly under the loader, because this is the same script tag —
              and because an agent's whole picture of who they are talking to
              comes from whatever this block chose to send. */}
          <div className="card">
            <div className="card__head">
              <span className="card__title">2 · Tell us who the customer is</span>
              <CopyButton value={visitorSnippet} />
            </div>
            <pre className="code">{visitorSnippet}</pre>
            <div className="card__foot">
              Set <code>window.SuperChat.visitor</code> before the loader runs. It reaches the
              agent as the name and address on the thread, and everything under
              <code> metadata</code> appears in the context panel under{' '}
              <strong>Custom metadata</strong> — which is the only place an agent can see the
              plan, the shop, or the app version without asking.
            </div>

            <div className="settings">
              <div className="settings__row">
                <span>Custom fields</span>
                <span className="mono muted">
                  up to {LIMITS.VISITOR_METADATA_MAX_KEYS} keys
                </span>
              </div>
              <div className="settings__row">
                <span>Key and value length</span>
                <span className="mono muted">
                  {LIMITS.VISITOR_METADATA_KEY_MAX} / {LIMITS.VISITOR_METADATA_VALUE_MAX} chars
                </span>
              </div>
              <div className="settings__row">
                <span>Nested objects and arrays</span>
                <span className="mono muted">dropped — flatten them first</span>
              </div>
            </div>

            <div className="card__foot">
              Nothing here authorises anything, so nothing here can fail a session: a malformed
              block costs you the detail, never the merchant&apos;s chat. Two habits are tolerated
              on purpose — custom fields written directly on <code>visitor</code> instead of under
              <code> metadata</code> are collected anyway, and an <code>email</code> that is not
              an address (a shop domain is the usual one) is kept as metadata rather than
              discarded. Values are re-read on every session, so a plan change shows up on the
              next page load rather than being frozen at the first one.
            </div>
          </div>

          <div className="card">
            <div className="card__head">
              <span className="card__title">3 · App keys</span>
            </div>
            <div className="table table--keys">
              <div className="table__head">
                <div>APP</div>
                <div>PUBLIC KEY</div>
                <div>ORIGINS</div>
                <div className="right">ROTATE</div>
              </div>
              <div className="table__body">
                {apps.map((app) => (
                  <div className="table__row" key={app.id}>
                    <div className="table__app" data-cell="app">
                      <span
                        className="row__stripe"
                        style={{ background: appIndex[app.id]?.color }}
                      />
                      <span className="ellipsis">{app.name}</span>
                    </div>
                    <div className="mono ellipsis" data-cell="key">{app.publicKey}</div>
                    <div className="mono muted" data-cell="origins">{app.allowedOrigins.length}</div>
                    <div className="right" data-cell="rotate">
                      {/* Owner-only: rotating breaks every deployed snippet at
                          once, which is not delegated with edit access. */}
                      {app.id === activeAppId && isAppOwner ? (
                        <button type="button" className="link-button" onClick={rotate}>
                          <RefreshCw size={12} /> Rotate
                        </button>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card__foot">
              The key grants nothing on its own — the session endpoint only honours it from an
              allowed origin, checked server-side as well as by CORS.
            </div>
          </div>

          <div className="card">
            <div className="card__head">
              <span className="card__title">4 · Allowed origins</span>
              {saving && <span className="card__meta">Saving…</span>}
            </div>
            <div className="origins">
              {origins.map((origin) => (
                <span className="chip" key={origin}>
                  <span className="mono">{origin}</span>
                  {canEdit && (
                    <button type="button" onClick={() => removeOrigin(origin)} aria-label="Remove">
                      <X size={10} />
                    </button>
                  )}
                </span>
              ))}
              {origins.length === 0 && (
                <span className="muted">
                  No origins yet — the widget cannot start a session until one is listed.
                </span>
              )}
            </div>
            {canEdit && (
              <form className="origins__add" onSubmit={addOrigin}>
                {/* Not admin.shopify.com: that is the frame an embedded app
                    sits inside, never the origin the widget runs on. */}
                <input
                  className="field__control mono"
                  value={originDraft}
                  onChange={(e) => setOriginDraft(e.target.value)}
                  placeholder="https://app.your-domain.com"
                  aria-label="Add allowed origin"
                />
                <button type="submit" className="button">
                  <Plus size={13} /> Add
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="integration__col">
          {/* First in this column, because it changes what the install column
              opposite says: the loader guidance and the metadata example both
              follow from what kind of app this is. */}
          <AppTypeCard canEdit={canEdit} />

          <div className="card">
            <div className="card__head">
              <span className="card__title">Widget appearance</span>
            </div>
            {settings && (
              <div className="settings">
                <label className="settings__row">
                  <span>Accent colour</span>
                  <span className="settings__control">
                    <input
                      type="color"
                      value={settings.accentColor}
                      disabled={!canEdit}
                      onChange={(e) => setSettings({ ...settings, accentColor: e.target.value })}
                      onBlur={() => saveApp({ settings: { accentColor: settings.accentColor } })}
                      aria-label="Accent colour"
                    />
                    <span className="mono">{settings.accentColor}</span>
                  </span>
                </label>

                <label className="settings__row settings__row--stack">
                  <span>Welcome message</span>
                  <textarea
                    className="field__control field__control--area"
                    rows={2}
                    disabled={!canEdit}
                    maxLength={500}
                    value={settings.welcomeMessage}
                    onChange={(e) => setSettings({ ...settings, welcomeMessage: e.target.value })}
                    onBlur={() =>
                      saveApp({ settings: { welcomeMessage: settings.welcomeMessage } })
                    }
                  />
                </label>

                <label className="settings__row">
                  <span>User alerts override mute</span>
                  <input
                    type="checkbox"
                    className="switch"
                    disabled={!canEdit}
                    checked={settings.emergencyOverridesMute}
                    onChange={(e) => {
                      setSettings({ ...settings, emergencyOverridesMute: e.target.checked });
                      saveApp({ settings: { emergencyOverridesMute: e.target.checked } });
                    }}
                  />
                </label>
              </div>
            )}
          </div>

          <div className="card card--danger">
            <div className="card__head">
              <span className="card__title danger">
                <AlertCircle size={13} /> User alerts
              </span>
            </div>
            <div className="card__foot">
              A user alert force-opens the chat on the merchant's screen, overriding their mute
              preference. To them it reads as an ordinary reply that happened to arrive with the
              chat open — the alert is visible on the agent side only. The server always pushes it,
              even when a socket is attached, because the tab may be backgrounded.
            </div>
            <div className="settings">
              <div className="settings__row">
                <span>Overrides merchant mute</span>
                <span className="mono">
                  {settings?.emergencyOverridesMute ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="settings__row">
                <span>Alert tone</span>
                <span className="mono">Two-tone chime ×3</span>
              </div>
              <div className="settings__row">
                <span>Daily cap per merchant</span>
                <span className="mono muted">Not enforced server-side</span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card__head">
              <span className="card__title">Your notifications</span>
            </div>
            <div className="card__foot">
              The server pushes to you when a visitor message lands and no agent socket is watching
              this app. Dead endpoints are pruned on the first 404/410.
            </div>
            <div className="settings">
              <div className="settings__row">
                <span>
                  Desktop alerts while the dashboard is open
                  <span className="muted"> — fires only when the tab is not focused</span>
                </span>
                <button
                  type="button"
                  className="button"
                  onClick={async () => {
                    if (desktopOn) {
                      setDesktopEnabled(false);
                      setDesktopOn(false);
                      return;
                    }
                    const result = await requestPermission();
                    if (result === 'granted') {
                      setDesktopEnabled(true);
                      setDesktopOn(true);
                    } else {
                      setPushError('Notification permission was not granted');
                    }
                  }}
                >
                  {desktopOn ? <BellOff size={13} /> : <Bell size={13} />}
                  {desktopOn ? 'Disable' : 'Enable'}
                </button>
              </div>

              <div className="settings__row">
                <span>Browser push</span>
                <button
                  type="button"
                  className="button"
                  onClick={togglePush}
                  disabled={!pushSupported()}
                >
                  {pushOn ? <BellOff size={13} /> : <Bell size={13} />}
                  {pushOn ? 'Disable' : 'Enable'}
                </button>
              </div>
              {!pushSupported() && (
                <div className="muted">This browser cannot deliver push notifications.</div>
              )}
              {pushError && <div className="login__error">{pushError}</div>}

              <div className="settings__row">
                <span>Notify all visitors</span>
                <span className="settings__control">
                  <button
                    type="button"
                    className="button"
                    onClick={() => notifyVisitors(false)}
                    disabled={testing}
                  >
                    <Send size={13} /> Notify
                  </button>
                  <button
                    type="button"
                    className="button button--danger"
                    onClick={() => notifyVisitors(true)}
                    disabled={testing}
                    title="Force-opens the chat on every merchant's screen"
                  >
                    <AlertCircle size={13} /> User alert
                  </button>
                </span>
              </div>

              {testResult && (
                <div className={testResult.ok ? 'result result--ok' : 'result result--warn'}>
                  {testResult.message}
                </div>
              )}

              <div className="muted">
                Goes to <strong>every visitor on this app</strong> — live over the socket to open
                widgets, and Web Push to every registered visitor device. Nothing is written to
                any conversation. The user-alert variant force-opens their chat over a muted
                preference, exactly as a real one does.
              </div>
            </div>
          </div>

          <SlackCard appId={activeAppId} canEdit={canEdit} />

          {/* Beside Slack, because it is the same kind of thing: this app's own
              connection to somebody else's service, holding a credential we
              never hand back. Distinct from the agent email alerts elsewhere on
              this page, which run on the deployment's own Resend. */}
          <ResendCard appId={activeAppId} canEdit={canEdit} />

          {/* Under the two channels it reports on, and above email — which is
              not tracked here because Resend owns delivery once it accepts a
              message, so there is no per-recipient outcome to retry against. */}
          <DeliveryStatusCard appId={activeAppId} canEdit={canEdit} />

          <div className="card">
            <div className="card__head">
              <span className="card__title">
                <Mail size={13} /> Email alerts
              </span>
              {emailSaving && <span className="card__meta">Saving…</span>}
            </div>
            <div className="card__foot">
              The only channel that reaches you with the browser closed. Sent to{' '}
              <span className="mono">{user?.email}</span>, and applies to{' '}
              <strong>every app you work on</strong> — this is an account setting, not an app one.
            </div>

            <div className="settings">
              <label className="settings__row">
                <span>
                  When a visitor comes online
                  <span className="muted"> — once per visitor per 10 minutes</span>
                </span>
                <input
                  type="checkbox"
                  className="switch"
                  checked={Boolean(emailPrefs.onVisitorArrival)}
                  disabled={emailSaving === 'onVisitorArrival'}
                  onChange={(e) => setEmailPref('onVisitorArrival', e.target.checked)}
                />
              </label>

              <label className="settings__row">
                <span>
                  When a message arrives and nobody is watching
                  <span className="muted"> — at most one per conversation per 15 minutes</span>
                </span>
                <input
                  type="checkbox"
                  className="switch"
                  checked={Boolean(emailPrefs.onNewMessage)}
                  disabled={emailSaving === 'onNewMessage'}
                  onChange={(e) => setEmailPref('onNewMessage', e.target.checked)}
                />
              </label>

              <label className="settings__row">
                <span>
                  When a conversation or ticket is assigned to you
                  <span className="muted"> — every time, never batched</span>
                </span>
                <input
                  type="checkbox"
                  className="switch"
                  checked={emailPrefs.onAssignment !== false}
                  disabled={emailSaving === 'onAssignment'}
                  onChange={(e) => setEmailPref('onAssignment', e.target.checked)}
                />
              </label>

              <div className="settings__row">
                <span>Send yourself a test</span>
                <button
                  type="button"
                  className="button"
                  onClick={sendTestEmail}
                  disabled={emailTesting}
                >
                  <Send size={13} /> {emailTesting ? 'Sending…' : 'Send test'}
                </button>
              </div>

              {emailResult && (
                <div className={emailResult.ok ? 'result result--ok' : 'result result--warn'}>
                  {emailResult.message}
                </div>
              )}

              {serverConfig && !serverConfig.emailEnabled && (
                <div className="result result--warn">
                  Email is not configured on this server. Set <code>RESEND_API_KEY</code> and
                  <code> EMAIL_FROM</code> and restart — these toggles save, but nothing will be
                  sent until then.
                </div>
              )}

              <div className="muted">
                Message alerts follow the same rule as push: they only go out when no agent socket
                is attached, so working in the dashboard never mails you. Muting a visitor in the
                context panel silences their email too — except emergencies, which always send.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IntegrationView;

